import { kycService } from '@/services/kycService';
import {
  KycAdminEditTokenValidationResponse,
  KycFormData,
  KycFormSubmissionResponse,
  KycTokenValidationResponse,
} from '@/types';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

/**
 * Hook for KYC token validation
 * Validates if a KYC token is valid and not expired
 *
 * @param token - The KYC token to validate
 * @param options - Query options
 * @returns Query result with token validation data
 */
export const useKycTokenValidation = (
  token: string,
  options?: { enabled?: boolean }
): UseQueryResult<KycTokenValidationResponse, Error> => {
  return useQuery({
    queryKey: ['validateKycToken', token],
    queryFn: () => kycService.validateKycToken(token),
    retry: false, // Don't retry token validation
    staleTime: 0, // Always check token freshness
    gcTime: 0, // Don't cache token validation results
    enabled: options?.enabled !== false && !!token, // Only enabled if token exists
  });
};

/**
 * Hook for KYC admin edit token validation
 * Validates the token and retrieves the current form data for admin editing
 *
 * @param token - The KYC admin edit token to validate
 * @param options - Query options
 * @returns Query result with token validation data including form data
 */
export const useKycAdminEditTokenValidation = (
  token: string,
  options?: { enabled?: boolean }
): UseQueryResult<KycAdminEditTokenValidationResponse, Error> => {
  return useQuery({
    queryKey: ['validateKycAdminEditToken', token],
    queryFn: () => kycService.validateAdminEditToken(token),
    retry: false, // Don't retry token validation
    staleTime: 0, // Always check token freshness
    gcTime: 0, // Don't cache token validation results
    enabled: options?.enabled !== false && !!token, // Only enabled if token exists
  });
};

/**
 * Hook for KYC form submission
 * Submits completed KYC form data to the backend
 *
 * @returns Mutation result for form submission
 */
export const useKycFormSubmission = (): UseMutationResult<
  KycFormSubmissionResponse,
  Error,
  KycFormData,
  unknown
> => {
  return useMutation({
    mutationFn: (formData: KycFormData) => kycService.submitKycForm(formData),
    // Optional: Add onSuccess, onError callbacks here if needed
  });
};

/**
 * Hook for KYC admin edit form submission
 * Submits KYC form data via the admin edit endpoint
 *
 * @returns Mutation result for admin edit form submission
 */
export const useKycAdminEditFormSubmission = (): UseMutationResult<
  KycFormSubmissionResponse,
  Error,
  KycFormData,
  unknown
> => {
  return useMutation({
    mutationFn: (formData: KycFormData) =>
      kycService.submitAdminEditKycForm(formData),
    // Optional: Add onSuccess, onError callbacks here if needed
  });
};
